import React, { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import PadlockIcon from 'components/Common/Icons/Padlock';
import PinkTickIcon from 'components/Common/Icons/PinkTick';
import { useCustomer } from 'hooks/useCustomer';
import { getRoutesUri, getUri } from 'services/generic';
import { ENV_COUNTRY } from 'settings/env';

import styles from './CheckoutNavbar.module.scss';

type Props = {
  classes?: Partial<{ container: string }>;
};

export default function CheckoutNavbar({ classes }: Props): JSX.Element {
  const { pathname } = useLocation();
  const { isLogged } = useCustomer();
  const pathWithoutCountry = pathname.replace(`/${ENV_COUNTRY}`, '');
  const navigationSteps = useMemo(
    () => [
      {
        stepNumber: 1,
        matcher: (currentUri: string) => currentUri === '/checkout/cart',
        stepLabel: 'LABEL_BASKET',
        stepLink: getUri('/checkout/cart'),
      },
      {
        stepNumber: 2,
        matcher: (currentUri: string) =>
          ['/checkout/account/login', '/checkout/account/create'].includes(
            currentUri
          ),
        stepLabel: 'LABEL_MY_ACCOUNT',
        stepLink: isLogged
          ? getRoutesUri<string>('customerAccountOrderHistory')
          : getRoutesUri<string>('checkoutLogin'),
      },
      {
        stepNumber: 3,
        matcher: (currentUri: string) => currentUri === '/checkout/shipping',
        stepLabel: 'LABEL_DELIVERY',
        stepLink: getRoutesUri<string>('checkoutShipping'),
      },
      {
        stepNumber: 4,
        matcher: (currentUri: string) => currentUri === '/checkout/payment',
        stepLabel: 'LABEL_SECURE_PAYMENT',
        stepLink: getUri('/checkout/payment'),
        beforeIcon: <PadlockIcon />,
        specialClass: styles.securePayment,
      },
    ],
    [!!isLogged] // Use "!!" to have only true or false as a value.
  );

  const current = useMemo(
    () => navigationSteps.find(step => step.matcher(pathWithoutCountry)),
    [pathWithoutCountry]
  ) as typeof navigationSteps[0];

  return (
    <nav className={classNames(styles.nav, classes?.container)}>
      <ul>
        {navigationSteps.map(navStep => {
          return (
            <li
              key={navStep.stepNumber}
              className={classNames({
                [styles.active]: navStep.stepNumber === current.stepNumber,
                [styles.validated]: navStep.stepNumber < current.stepNumber,
              })}
            >
              <CheckoutStep
                stepLink={navStep.stepLink}
                stepNumber={navStep.stepNumber}
                stepLabel={navStep.stepLabel}
                beforeIcon={navStep.beforeIcon}
                specialClass={navStep.specialClass}
              />
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function CheckoutStep({
  stepLink,
  stepNumber,
  stepLabel,
  beforeIcon,
  specialClass,
}: {
  stepLink: string;
  stepNumber: number;
  stepLabel: string;
  beforeIcon?: React.ReactNode;
  specialClass?: string;
}): JSX.Element {
  return (
    <Link to={stepLink}>
      <hr />
      <div className={styles.stepNumber}>{stepNumber}</div>
      <div className={styles.stepLabel}>
        <span className={styles.pinkTick}>
          <PinkTickIcon />
        </span>
        {beforeIcon}
        <div className={specialClass}>
          <Trans i18nKey={stepLabel} />
        </div>
      </div>
    </Link>
  );
}
